<template>
    <base-dialog :loading="loading" :value="dialog" :title="dialogTitle" :width="480" :fullscreen="isMobile" @input="onDialogInput" @close="hide">
        <v-row dense>
            <v-col cols="12">
                <kimsa-date-picker :label="$t('common.date_injury')" solo flat dense force-value color="secondary"
                    @change="date = $event" :value="date" />
            </v-col>
            <v-col cols="12">
                <kimsa-combobox :label="$t('common.injury')" :placeholder="$t('common.select_placeholder')" dense
                    no-object item-text="label" :items="typeOptions" @change="type = $event;" :value="type" />
            </v-col>
            <v-col cols="12">
                <kimsa-combobox :label="$t('common.zone')" :placeholder="$t('common.select_placeholder')" dense
                    no-object item-text="label" :items="zoneOptions" @change="zone = $event;" :value="zone" />
            </v-col>
            <v-col cols="12">
                <kimsa-combobox :label="$t('common.condition')" :placeholder="$t('common.select_placeholder')" dense
                    no-object item-text="label" :items="conditionOptions" @change="condition = $event;" :value="condition" />
            </v-col>
            <v-col cols="12">
                <kimsa-combobox :label="$t('common.was_it')" :placeholder="$t('common.select_placeholder')" dense
                    no-object item-text="label" :items="wasItOptions" @change="wasIt = $event;" :value="wasIt" />
            </v-col>
            <v-col cols="12">
                <kimsa-date-picker :label="$t('common.discharge_date')" solo flat dense force-value color="secondary"
                    @change="dischargeDate = $event" :value="dischargeDate" />
            </v-col>
            <v-col cols="12">
                <kimsa-textarea-field no-elevation :label="$t('common.comment')" @change="comment = $event"
                    :value="comment" force-value />
            </v-col>
            <v-col cols="12" v-if="isCoach">
                <kimsa-combobox :label="$t('common.complexity')" :placeholder="$t('common.select_placeholder')" dense
                    force-value no-object item-text="label" :items="complexityOptions" @change="complexity = $event;"
                    :value="complexity" />
            </v-col>
        </v-row>
        <template #footer>
            <v-btn class="footer-btn" depressed block :disabled="disabled" @click="save" :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import { sendMessage, MESSAGE_COLORS } from "@/utils/Messages";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import BaseDialog from "@/components/common/BaseDialog";

export default {
    name: "InjuryFormDialog",
    components: { KimsaTextareaField, KimsaCombobox, KimsaDatePicker, BaseDialog },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        studentId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            injury: null,
            editMode: false,
            date: null,
            type: null,
            zone: null,
            condition: null,
            wasIt: null,
            dischargeDate: null,
            comment: '',
            complexity: null,
        }
    },
    computed: {
        ...mapState(['config']),
        dialogTitle(vm = this) {
            return `${vm.editMode ? vm.$t('common.edit') : vm.$t('common.add')} ${vm.$t('common.injury')}`
        },
        disabled(vm = this) {
            return !vm.date || !vm.type || !vm.zone || !vm.condition || !vm.wasIt
        },
        typeOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.type.map(value => ({ name: value, value, label: vm.injuryTypeLabel(value) }))
        },
        zoneOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.zone.map(value => ({ name: value, value, label: vm.injuryZoneLabel(value) }))
        },
        conditionOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.condition.map(value => ({ name: value, value, label: vm.injuryConditionLabel(value) }))
        },
        wasItOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.wasIt.map(value => ({ name: value, value, label: vm.injuryMomentLabel(value) }))
        },
        complexityOptions(vm = this) {
            if (!vm.config) return []
            return vm.config.injuries.complexity.map(value => ({ name: value, value, label: vm.injuryComplexityLabel(value) }))
        },
        isCoach(vm = this) {
            return vm.accountType === 'coach'
        },
        isStudent(vm = this) {
            return vm.accountType === 'student'
        }
    },
    async mounted() {
        const vm = this
        if (!vm.config) await vm.bindConfig()
    },
    methods: {
        ...mapActions(['bindConfig']),
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onDialogInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.injury = null
            vm.editMode = false
            vm.date = null
            vm.type = null
            vm.zone = null
            vm.condition = null
            vm.wasIt = null
            vm.dischargeDate = null
            vm.comment = ''
            vm.complexity = null
        },
        loadInjury(injury) {
            const vm = this
            vm.injury = injury
            vm.editMode = true
            vm.date = injury.date
            vm.type = injury.type
            vm.zone = injury.zone
            vm.condition = injury.condition
            vm.wasIt = injury.wasIt
            vm.dischargeDate = injury?.dischargeDate || null
            vm.comment = injury.comment || ''
            vm.complexity = injury?.complexity || null
            vm.show()
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                let hasInjury = !vm.dischargeDate || (vm.dischargeDate && vm.$moment(vm.dischargeDate).isAfter(vm.$moment()))

                let payload = {
                    date: vm.date,
                    type: vm.type,
                    zone: vm.zone,
                    condition: vm.condition,
                    wasIt: vm.wasIt,
                    dischargeDate: vm.dischargeDate,
                    comment: vm.comment,
                    complexity: vm.complexity
                }

                if (vm.editMode) {
                    await db.collection('injuries').doc(vm.injury.id).update(payload)
                    vm.toast(vm.$t('common.injury_edited'))
                } else {
                    payload = {
                        studentId: vm.studentId,
                        ...payload
                    }
                    let response = await db.collection('injuries').add(payload)
                    if (vm.isStudent) await vm.sendMessageToCoach(response.id)
                    vm.toast(vm.$t('common.injury_added'))
                }

                await db.collection('users').doc(vm.studentId).update({
                    hasInjury: hasInjury,
                    dischargeInjutyDate: vm.dischargeDate || null,
                })

                vm.hide()
                vm.$emit('success')
            })
        },
        async sendMessageToCoach(injuryId = null) {
            const vm = this

            let message = `${vm.$t('common.date_injury')}: ${vm.$moment(vm.date).format('DD-MM-YYYY')} \n${vm.$t('common.injury')}: ${vm.injuryTypeLabel(vm.type)} \n${vm.$t('common.zone')}: ${vm.injuryZoneLabel(vm.zone)} \n${vm.$t('common.condition')}: ${vm.injuryConditionLabel(vm.condition)} \n${vm.$t('common.was_it')}: ${vm.injuryMomentLabel(vm.wasIt)} \n${vm.$t('common.discharge_date')}: ${vm.$moment(vm.date).format('DD-MM-YYYY')}`
            if (vm.comment) message += `\n${vm.$t('common.comment')}: ${vm.comment}`

            let studentId = vm.studentId
            let coachId = vm.coachId

            await sendMessage({ message, from: studentId, type: 'injury', injuryId, color: MESSAGE_COLORS.injury }, coachId, studentId)
        },
    },

}
</script>

<style scoped>
.footer-btn.v-btn {
    height: 52px !important;
    border-radius: 0 !important;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 700;
    box-shadow: none !important;
    background: var(--v-primary-base) !important;
    color: #0C0E12 !important;
}

.footer-btn.v-btn.v-btn--disabled {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtMuted-base) !important;
}
</style>